import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { Button } from "../ui/button";
import { Play, X, Trophy, AlertTriangle, Smartphone, Info, Flame, Calendar } from "lucide-react";

/**
 * Screen Break Challenge — the user commits to NOT using their phone for N minutes.
 *
 * Behaviour tuned with the user (iter50):
 *   - Locking the phone → timer KEEPS running (timer is Date.now()-anchored, so
 *     real elapsed time is always accurate even when JS is suspended).
 *   - Leaving the app (switching to another app / browser tab) for more than
 *     HIDDEN_LEAVE_THRESHOLD_MS → challenge FAILS with "Desconexión digital no
 *     superada".
 *
 * Distinction heuristic: on `visibilitychange` → "hidden" we record hiddenAt.
 * On `visibilitychange` → "visible" we look at how long we were hidden:
 *   - If the goal was already reached during the hidden period → success.
 *   - Else if the hidden duration exceeds the threshold → the user actively
 *     used another app while the challenge was in progress → FAIL.
 *   - Else (brief peek / quick lock-unlock) → keep running.
 *
 * Why this works for phone lock: when the user locks the phone, they typically
 * don't unlock until either (a) the challenge is done (elapsed >= goal → success)
 * or (b) they briefly peek at notifications and relock (hiddenDuration tiny).
 * A true app-switch always involves a meaningful visible-in-another-app period.
 *
 * Persistence:
 *   _ft_screen_break_streak   — consecutive successes (reset on fail)
 *   _ft_screen_break_total    — cumulative screen-free minutes (all time)
 *   _ft_screen_break_history  — { "YYYY-MM-DD": minutes } per day (weekly stats)
 */
const PRESET_MINUTES = [5, 10, 15, 25, 45];
const HIDDEN_LEAVE_THRESHOLD_MS = 3000; // > 3s hidden (and not complete) = left app

const LS_KEY_STREAK = "_ft_screen_break_streak";
const LS_KEY_TOTAL = "_ft_screen_break_total";
const LS_KEY_HISTORY = "_ft_screen_break_history";

const readHistory = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY_HISTORY) || "{}"); }
  catch { return {}; }
};

const writeHistory = (history) => {
  try { localStorage.setItem(LS_KEY_HISTORY, JSON.stringify(history)); }
  catch (e) { console.error("screen-break history write failed:", e); }
};

const todayKey = () => new Date().toISOString().slice(0, 10);

const getWeekMinutes = () => {
  const history = readHistory();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let total = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date(today); d.setDate(today.getDate() - i);
    total += history[d.toISOString().slice(0, 10)] || 0;
  }
  return total;
};

const ScreenBreakChallenge = ({ onClose }) => {
  const { t } = useLanguage();
  const [goalMinutes, setGoalMinutes] = useState(10);
  const [phase, setPhase] = useState("setup");   // setup | running | success | failed
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [streak, setStreak] = useState(() => parseInt(localStorage.getItem(LS_KEY_STREAK) || "0", 10) || 0);
  const [totalMinutes, setTotalMinutes] = useState(() => parseInt(localStorage.getItem(LS_KEY_TOTAL) || "0", 10) || 0);
  const [weekMinutes, setWeekMinutes] = useState(() => getWeekMinutes());
  const timerRef = useRef(null);
  const completedRef = useRef(false);
  const hiddenAtRef = useRef(null);

  const start = () => {
    setPhase("running");
    setStartedAt(Date.now());
    setElapsedMs(0);
    completedRef.current = false;
    hiddenAtRef.current = null;
  };

  const cancelChallenge = () => {
    if (phase !== "running") return;
    setPhase("failed");
    localStorage.setItem(LS_KEY_STREAK, "0");
    setStreak(0);
  };

  const failChallenge = () => {
    if (phase !== "running" || completedRef.current) return;
    completedRef.current = true;
    setPhase("failed");
    localStorage.setItem(LS_KEY_STREAK, "0");
    setStreak(0);
  };

  const completeChallenge = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setPhase("success");
    const newStreak = streak + 1;
    const newTotal = totalMinutes + goalMinutes;
    localStorage.setItem(LS_KEY_STREAK, String(newStreak));
    localStorage.setItem(LS_KEY_TOTAL, String(newTotal));
    const history = readHistory();
    const key = todayKey();
    history[key] = (history[key] || 0) + goalMinutes;
    writeHistory(history);
    setStreak(newStreak);
    setTotalMinutes(newTotal);
    setWeekMinutes(getWeekMinutes());
  };

  // Timer tick — Date.now() anchored, accurate even across JS throttling.
  useEffect(() => {
    if (phase !== "running" || !startedAt) return;
    const goalMs = goalMinutes * 60 * 1000;
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setElapsedMs(elapsed);
      if (elapsed >= goalMs) {
        clearInterval(timerRef.current);
        completeChallenge();
      }
    }, 250);

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === "visible" && hiddenAtRef.current != null) {
        const hiddenDurationMs = Date.now() - hiddenAtRef.current;
        hiddenAtRef.current = null;
        const totalElapsed = Date.now() - startedAt;
        // Priority #1 — the goal was reached while hidden: success regardless.
        if (totalElapsed >= goalMs) {
          clearInterval(timerRef.current);
          completeChallenge();
          return;
        }
        // Priority #2 — the user was away too long for just a phone-lock peek:
        // they almost certainly opened another app → fail.
        if (hiddenDurationMs > HIDDEN_LEAVE_THRESHOLD_MS) {
          clearInterval(timerRef.current);
          failChallenge();
          return;
        }
        // Priority #3 — brief peek (<3s): keep running, re-sync elapsed.
        setElapsedMs(totalElapsed);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, startedAt, goalMinutes]);

  const remainingSec = Math.max(0, goalMinutes * 60 - Math.floor(elapsedMs / 1000));
  const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
  const ss = String(remainingSec % 60).padStart(2, "0");
  const progressPct = Math.min(100, (elapsedMs / (goalMinutes * 60 * 1000)) * 100);

  return (
    <div className="bg-[#0a0a0f] rounded-3xl p-8 border border-white/10" data-testid="screen-break-tool">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="font-['Outfit'] text-2xl font-semibold mb-1">{t("tool_screenbreak_title")}</h2>
          <p className="text-white/50 text-sm">{t("tool_screenbreak_subtitle")}</p>
        </div>
        {phase !== "running" && (
          <button onClick={onClose} className="text-white/40 hover:text-white p-2" data-testid="screenbreak-close-btn" aria-label="close">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {phase === "setup" && (
        <div>
          {/* Stats: streak / week / total */}
          <div className="grid grid-cols-3 gap-2 mb-6" data-testid="screenbreak-stats">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center" data-testid="screenbreak-streak">
              <Flame className="w-5 h-5 mx-auto mb-1 text-amber-400" />
              <p className="text-white font-['Outfit'] font-semibold text-xl" data-testid="screenbreak-streak-value">{streak}</p>
              <p className="text-white/40 text-[10px] uppercase tracking-wider">{t("tool_screenbreak_streak")}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center" data-testid="screenbreak-week">
              <Calendar className="w-5 h-5 mx-auto mb-1 text-cyan-400" />
              <p className="text-white font-['Outfit'] font-semibold text-xl" data-testid="screenbreak-week-value">{weekMinutes}</p>
              <p className="text-white/40 text-[10px] uppercase tracking-wider">{t("tool_screenbreak_week_min")}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center" data-testid="screenbreak-total">
              <Smartphone className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
              <p className="text-white font-['Outfit'] font-semibold text-xl" data-testid="screenbreak-total-value">{totalMinutes}</p>
              <p className="text-white/40 text-[10px] uppercase tracking-wider">{t("tool_screenbreak_total_min")}</p>
            </div>
          </div>

          <p className="text-white/70 text-sm mb-4">{t("tool_screenbreak_pick_time")}</p>
          <div className="grid grid-cols-5 gap-2 mb-6">
            {PRESET_MINUTES.map((m) => (
              <button
                key={m}
                onClick={() => setGoalMinutes(m)}
                className={`py-3 rounded-xl border text-sm font-semibold transition ${goalMinutes === m ? "bg-cyan-400/15 border-cyan-400 text-cyan-200" : "bg-white/[0.03] border-white/10 text-white/60 hover:border-white/30"}`}
                data-testid={`screenbreak-preset-${m}`}
              >
                {m} {t("tool_screenbreak_min")}
              </button>
            ))}
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-6 flex gap-2">
            <Info className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
            <p className="text-amber-100/90 text-xs leading-relaxed">{t("tool_screenbreak_rules")}</p>
          </div>

          <div className="flex justify-center">
            <Button onClick={start} className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90 text-white rounded-full px-8 py-5 font-semibold" data-testid="screenbreak-start-btn">
              <Play className="w-4 h-4 mr-2" /> {t("tool_screenbreak_start")} ({goalMinutes} {t("tool_screenbreak_min")})
            </Button>
          </div>
        </div>
      )}

      {phase === "running" && (
        <div className="py-8 text-center" data-testid="screenbreak-running">
          <div className="relative w-56 h-56 mx-auto mb-6">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
              <circle cx="50" cy="50" r="45" fill="none" stroke="url(#gradBreak)" strokeWidth="3" strokeDasharray="282.7" strokeDashoffset={282.7 - (282.7 * progressPct) / 100} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.3s linear" }} />
              <defs>
                <linearGradient id="gradBreak" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="100%" stopColor="#06b6d4" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-white font-['Outfit'] font-semibold text-5xl tracking-tight" data-testid="screenbreak-timer">{mm}:{ss}</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-1">{t("tool_screenbreak_remaining")}</p>
            </div>
          </div>
          <p className="text-white/70 text-sm max-w-sm mx-auto mb-3">{t("tool_screenbreak_keep_here")}</p>
          <p className="text-emerald-200/80 text-xs max-w-sm mx-auto mb-2">{t("tool_screenbreak_lock_ok")}</p>
          <p className="text-amber-200/80 text-xs max-w-sm mx-auto mb-6">{t("tool_screenbreak_leave_fails")}</p>
          <Button onClick={cancelChallenge} variant="ghost" className="text-white/50 hover:text-white/80 text-xs" data-testid="screenbreak-cancel-btn">
            {t("tool_screenbreak_cancel")}
          </Button>
        </div>
      )}

      {phase === "success" && (
        <div className="py-8 text-center" data-testid="screenbreak-success">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
            <Trophy className="w-8 h-8 text-emerald-300" />
          </div>
          <h3 className="font-['Outfit'] text-xl font-semibold mb-2">{t("tool_screenbreak_success_title")}</h3>
          <p className="text-white/60 text-sm mb-4">{t("tool_screenbreak_success_msg").replace("{minutes}", String(goalMinutes))}</p>
          <p className="text-cyan-300 text-sm mb-6">{t("tool_screenbreak_streak")}: {streak}</p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => setPhase("setup")} variant="ghost" className="border border-white/20 text-white rounded-full px-6" data-testid="screenbreak-again-btn">{t("tool_screenbreak_again")}</Button>
            <Button onClick={onClose} className="bg-white text-black hover:bg-white/90 rounded-full px-6" data-testid="screenbreak-success-close-btn">{t("tool_done")}</Button>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="py-8 text-center" data-testid="screenbreak-failed">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 border border-red-400/40 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-300" />
          </div>
          <h3 className="font-['Outfit'] text-xl font-semibold mb-2" data-testid="screenbreak-failed-title">{t("tool_screenbreak_fail_title")}</h3>
          <p className="text-white/60 text-sm mb-6 max-w-sm mx-auto">{t("tool_screenbreak_fail_msg")}</p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => { completedRef.current = false; setPhase("setup"); }} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full px-6" data-testid="screenbreak-retry-btn">{t("tool_screenbreak_retry")}</Button>
            <Button onClick={onClose} variant="ghost" className="border border-white/20 text-white rounded-full px-6" data-testid="screenbreak-fail-close-btn">{t("tool_close")}</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenBreakChallenge;
